/**
 * Showcase video URL parsing — shared by the profile editor (validates what an
 * admin pastes) and the public Overview tab (turns the stored URL into an embed).
 *
 * Both sides must agree, or the editor accepts a link the profile then cannot
 * play. Before this module each had its own regex that recognised only
 * `watch?v=`, `youtu.be/` and `/embed/`, so a YouTube Short, a `/live/` link, or
 * a share URL where `v` is not the first query parameter was rejected in the
 * editor as "not a valid YouTube URL" even though YouTube embeds all of them.
 */

export type VideoProvider = "youtube" | "vimeo";

export interface ParsedVideo {
  provider: VideoProvider;
  id: string;
  /** iframe src */
  embedUrl: string;
  /** Canonical watch page, for "Open on YouTube" links. */
  watchUrl: string;
  /** Poster image where the provider serves one without an API call. */
  thumbnailUrl: string | null;
}

const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_HOSTS = new Set(["youtube.com", "youtube-nocookie.com", "youtu.be"]);
/** Path prefixes that carry the video id as the next segment. */
const YOUTUBE_PATH_PREFIXES = new Set(["embed", "shorts", "live", "v", "e"]);

const stripSubdomain = (host: string) => host.toLowerCase().replace(/^(www|m|mobile|music)\./, "");

function toUrl(raw: string): URL | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const candidate = /^[a-z]+:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(candidate);
    return url.protocol === "https:" || url.protocol === "http:" ? url : null;
  } catch {
    return null;
  }
}

function youtubeIdFromUrl(url: URL): string | null {
  const host = stripSubdomain(url.hostname);
  if (!YOUTUBE_HOSTS.has(host)) return null;
  const segments = url.pathname.split("/").filter(Boolean);

  if (host === "youtu.be") {
    const id = segments[0];
    return id && YOUTUBE_ID_RE.test(id) ? id : null;
  }

  // /watch?v=<id> — `v` anywhere in the query, not only first.
  if (segments[0] === "watch") {
    const id = url.searchParams.get("v");
    return id && YOUTUBE_ID_RE.test(id) ? id : null;
  }

  // /embed/<id>, /shorts/<id>, /live/<id>, /v/<id>
  if (segments.length >= 2 && YOUTUBE_PATH_PREFIXES.has(segments[0])) {
    const id = segments[1];
    return YOUTUBE_ID_RE.test(id) ? id : null;
  }

  // Bare /<id> is not a YouTube shape we recognise; refuse rather than guess.
  return null;
}

function vimeoIdFromUrl(url: URL): string | null {
  const host = stripSubdomain(url.hostname);
  if (host !== "vimeo.com" && host !== "player.vimeo.com") return null;
  const segments = url.pathname.split("/").filter(Boolean);
  // vimeo.com/<id>, vimeo.com/<id>/<hash>, player.vimeo.com/video/<id>,
  // vimeo.com/channels/<name>/<id>, vimeo.com/groups/<name>/videos/<id>
  const numeric = segments.find((s) => /^\d{6,}$/.test(s));
  return numeric ?? null;
}

/**
 * Parse a pasted YouTube or Vimeo link. Returns null for anything else,
 * including an empty string — callers decide whether empty means "no video".
 */
export function parseVideoUrl(raw: string | null | undefined): ParsedVideo | null {
  if (!raw) return null;
  const url = toUrl(raw);
  if (!url) return null;

  const youtubeId = youtubeIdFromUrl(url);
  if (youtubeId) {
    return {
      provider: "youtube",
      id: youtubeId,
      embedUrl: `https://www.youtube.com/embed/${youtubeId}`,
      watchUrl: `https://www.youtube.com/watch?v=${youtubeId}`,
      thumbnailUrl: `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`,
    };
  }

  const vimeoId = vimeoIdFromUrl(url);
  if (vimeoId) {
    return {
      provider: "vimeo",
      id: vimeoId,
      embedUrl: `https://player.vimeo.com/video/${vimeoId}`,
      watchUrl: `https://vimeo.com/${vimeoId}`,
      thumbnailUrl: null,
    };
  }

  return null;
}
